import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { company } from "@/data/site";
import { useT, useContent } from "@/i18n";

export default function About() {
  const t = useT();
  const { vision, mission, activityAreas, stats, certificates } = useContent();

  return (
    <div data-testid="about-page">
      <PageHeader
        title={t("about.headerTitle")}
        crumb={t("about.headerTitle")}
        subtitle={t("about.headerSubtitle", { legal: company.legal, since: company.since })}
        image="/sections/hero-sanayi.jpg"
      />

      {/* INTRO */}
      <section className="py-28 md:py-36 bg-paper">
        <div className="container-kyt grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div>
            <SectionHeading eyebrow={t("about.introEyebrow")} title={t("about.introTitle")} desc={t("about.introDesc")} />
            <Reveal delay={150}>
              <p className="mt-6 text-[15.5px] leading-relaxed text-slate-600 font-light">{vision.extra}</p>
            </Reveal>
          </div>
          <Reveal className="grid grid-cols-2 gap-x-12 gap-y-10">
            {stats.map((s) => (
              <div key={s.label} className="border-t border-ink/15 pt-6">
                <div className="font-display text-4xl lg:text-5xl font-bold text-ink">
                  {s.value}<span className="text-brand-blue">{s.suffix}</span>
                </div>
                <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-slate-400 mt-2.5">{s.label}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="py-28 md:py-36 bg-white border-y border-ink/10">
        <div className="container-kyt grid md:grid-cols-2 gap-16 lg:gap-24">
          <Reveal>
            <div className="eyebrow mb-6">{vision.title}</div>
            <p className="font-display text-[1.7rem] sm:text-[2.1rem] text-ink leading-[1.25]">{vision.text}</p>
          </Reveal>
          <Reveal delay={100} className="md:border-l border-ink/10 md:pl-16 lg:pl-24">
            <div className="eyebrow mb-6">{mission.title}</div>
            <ul className="space-y-px">
              {mission.items.map((m, i) => (
                <li key={m} className="flex items-start gap-5 py-5 border-t border-ink/10 text-[15.5px] text-slate-700 leading-relaxed">
                  <span className="font-mono text-xs text-brand-blue pt-1 shrink-0">0{i + 1}</span> {m}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ACTIVITY AREAS */}
      <section className="py-28 md:py-36 bg-paper">
        <div className="container-kyt">
          <SectionHeading center eyebrow={t("about.actEyebrow")} title={activityAreas.title} desc={activityAreas.intro} />
          <div className="mt-16 max-w-3xl mx-auto">
            {activityAreas.items.map((it, i) => (
              <Reveal key={it} delay={i * 70}>
                <div className="group flex items-baseline gap-7 py-7 border-t border-ink/12 last:border-b">
                  <span className="font-mono text-sm text-slate-400 group-hover:text-brand-blue transition-colors w-8 shrink-0">0{i + 1}</span>
                  <span className="font-display text-xl sm:text-2xl text-ink group-hover:translate-x-1.5 transition-transform duration-300">{it}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATES */}
      <section className="py-28 md:py-36 bg-white border-t border-ink/10">
        <div className="container-kyt">
          <SectionHeading eyebrow={t("about.certEyebrow")} title={t("about.certTitle")} />
          <div className="mt-14 grid md:grid-cols-3 gap-10">
            {certificates.map((c, i) => (
              <Reveal key={c.code} delay={i * 90}>
                <div className="border-t border-ink/15 pt-8">
                  <div className="font-display text-2xl font-bold text-ink">{c.code}</div>
                  <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-brand-blue mt-2.5">{c.name}</div>
                  <p className="text-[13.5px] text-slate-500 mt-4 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={150}>
            <div className="mt-16 flex flex-wrap items-center gap-10">
              <Link to="/projeler" className="group inline-flex items-center gap-3 text-ink font-medium">
                <span className="border-b border-ink/30 pb-1 group-hover:border-brand-blue transition-colors">{t("common.exploreProjects")}</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href={`mailto:${company.email}?subject=${encodeURIComponent(t("common.getQuote"))}`} className="group inline-flex items-center gap-3 text-ink font-medium">
                <Mail className="h-5 w-5 text-brand-blue" />
                <span className="border-b border-ink/30 pb-1 group-hover:border-brand-blue transition-colors">{t("about.reachUs")}</span>
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
